import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { SessionBar } from '../SessionBar';
import { AdminAccessNotice } from './AdminAccessNotice';
import { OverviewPage } from './OverviewPage';
import { RunsPage } from './RunsPage';
import { SessionsPage } from './SessionsPage';
/**
 * 系統區的三段。**這份常數是分頁列的唯一真相**：
 * 網址白名單（`shell/routes.ts` 的 `SYSTEM_TABS`）與側欄二級列（`ConsoleShell` 的
 * `SYSTEM_NAV`）都跟著這裡的 id 走，加減段只改這三個地方。
 */
export const SYSTEM_TABS = [
    { id: 'overview', label: '總覽', code: 'A1' },
    { id: 'sessions', label: '工作階段', code: 'A3' },
    { id: 'monitor', label: '監控績效', code: 'A6' },
    { id: 'rooms-map', label: '房間地圖', code: 'A10' },
    { id: 'params', label: '運行參數', code: 'A7' },
    { id: 'grants', label: '聊手權限', code: 'A8' },
    { id: 'invites', label: '帳號邀請', code: 'A9' },
    { id: 'ai', label: 'AI 成效', code: 'A5' },
];
function systemTabGate(tab) {
    switch (tab) {
        case 'overview':
        case 'sessions':
        case 'ai':
        case 'monitor':
        case 'rooms-map':
        case 'params':
            return 'session';
        case 'grants':
        case 'invites':
            return 'admin-only';
        default: {
            const _exhaustive = tab;
            return _exhaustive;
        }
    }
}
/** T04 之前的名字；留著是為了外部既有 import 不必同時改（內容同 `SYSTEM_TABS`）。 */
export const ADMIN_TABS = SYSTEM_TABS;
/**
 * 依 `tab` 選內容。
 * `OverviewPage` 仍會丟 `'ops'`／`'accounts'`（那是它自家的 `AdminOverviewTab` 聯集，還沒收緊）：
 * `'ops'` 現在改指向「工作階段」（併頁後的落點），`'accounts'` 已無此段，吞掉。
 */
function pageFor(tab, client, onNavigate, unreadLive, aiContent, controlContent) {
    switch (tab) {
        case 'ai':
            return aiContent;
        case 'monitor':
            return controlContent?.monitor ?? _jsx("p", { className: "p-3 text-sm", children: "\u76E3\u63A7\u9762\u677F\u672A\u6CE8\u5165" });
        case 'rooms-map':
            return controlContent?.['rooms-map'] ?? _jsx("p", { className: "p-3 text-sm", children: "\u623F\u9593\u5730\u5716\u672A\u6CE8\u5165" });
        case 'params':
            return controlContent?.params ?? _jsx("p", { className: "p-3 text-sm", children: "\u53C3\u6578\u9762\u677F\u672A\u6CE8\u5165" });
        case 'grants':
            return controlContent?.grants ?? _jsx("p", { className: "p-3 text-sm", children: "\u6B0A\u9650\u9762\u677F\u672A\u6CE8\u5165" });
        case 'invites':
            return controlContent?.invites ?? _jsx("p", { className: "p-3 text-sm", children: "\u9080\u8ACB\u9762\u677F\u672A\u6CE8\u5165" });
        case 'sessions':
            /*
             * 兩塊同一頁、同一個捲動容器：上面是「現在連著的工作階段」，下面是
             * 「執行紀錄與健康」（`RunsPage` 的表格與健康檢查原封不動）。資料來源各自照舊，
             * T04 只改結構、不動 fetch。
             */
            return (_jsxs("div", { className: "flex flex-col gap-3", "data-testid": "system-sessions-merged", children: [_jsx(SessionsPage, { client: client }), _jsxs("div", { className: "cui-panel flex flex-col gap-2 p-3", "data-testid": "system-runs-block", children: [_jsx("h2", { className: "cui-panel-title", children: "\u4EFB\u52D9\u8207\u7CFB\u7D71\u30FB\u57F7\u884C\u7D00\u9304\u8207\u5065\u5EB7" }), _jsx(RunsPage, { client: client })] })] }));
        case 'overview':
            return (_jsx(OverviewPage, { client: client, onNavigate: (next) => {
                    if (next === 'accounts')
                        return;
                    if (next === 'ops') {
                        onNavigate('sessions');
                        return;
                    }
                    onNavigate(next);
                }, unreadLive: unreadLive }));
        default: {
            const _exhaustive = tab;
            return _exhaustive;
        }
    }
}
export function AdminShell({ tab, onTabChange, client, unreadLive, session, aiContent, controlContent, canAdmin = false, staffSession = false, onBack, }) {
    /**
     * token 存好之後 +1，讓目前分頁整個重掛 → 各頁的 `useEffect([client])` 重新 fetch。
     * 不用 `location.reload()`：那會連 `useInbox` 的訂閱一起重來（PLAN R3）。
     */
    const [reloadKey, setReloadKey] = useState(0);
    const navTabs = SYSTEM_TABS.filter((item) => systemTabGate(item.id) !== 'admin-only' || canAdmin);
    const current = SYSTEM_TABS.find((item) => item.id === tab) ?? navTabs[0];
    return (_jsxs("div", { className: "flex min-h-0 flex-1 flex-col gap-2 p-3", "data-testid": "admin-shell", "data-admin-tab": tab, children: [_jsxs("div", { className: "flex items-center justify-between gap-2", "data-testid": "system-breadcrumb", "data-section": tab, children: [_jsxs("div", { className: "flex items-baseline gap-1.5 text-[12px]", children: [_jsx("span", { className: "cui-muted", children: "\u7CFB\u7D71" }), _jsx("span", { className: "cui-muted", children: "/" }), _jsx("span", { className: "cui-accent", "data-testid": "system-breadcrumb-current", children: current.label })] }), onBack ? (_jsx("button", { type: "button", className: "cui-btn cui-btn--ghost cui-btn--sm", onClick: onBack, "data-testid": "system-back", children: "\u2190 \u56DE\u5DE5\u4F5C\u53F0" })) : null] }), _jsx("nav", { className: "cui-tabs w-full max-w-[560px]", role: "tablist", "aria-label": "\u7CFB\u7D71\u5206\u9801", children: navTabs.map((item) => {
                    const active = item.id === tab;
                    return (_jsx("button", { type: "button", role: "tab", id: `admin-tab-${item.id}`, "aria-selected": active, "aria-controls": `admin-panel-${item.id}`, className: `cui-tab${active ? ' is-active' : ''}`, onClick: () => onTabChange(item.id), "data-testid": "admin-tab", "data-tab": item.id, children: item.label }, item.id));
                }) }), _jsx(SessionBar, { ...(session ?? {}) }), _jsxs("div", { className: "flex min-h-0 flex-1 flex-col overflow-y-auto", id: `admin-panel-${tab}`, role: "tabpanel", "aria-labelledby": `admin-tab-${tab}`, children: [_jsx(AdminAccessNotice, { needsAdminToken: false, staffSession: staffSession, onSaved: () => setReloadKey((key) => key + 1), ...(session?.onRelogin === undefined ? {} : { onRelogin: session.onRelogin }) }), pageFor(tab, client, onTabChange, unreadLive ?? null, aiContent, controlContent)] }, `${tab}-${reloadKey}`)] }));
}
//# sourceMappingURL=AdminShell.js.map